import { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';

const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ filename: '', url: '', file_type: 'image', alt_text: '' });

  useEffect(() => { fetchMedia(); }, []);

  const fetchMedia = async () => {
    try { const res = await adminApi.getMedia(); setMedia(res.data); }
    catch (error) { toast.error('Failed to fetch media'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.url) { toast.error('URL required'); return; }
    try {
      await adminApi.createMedia({ ...formData, filename: formData.filename || formData.url.split('/').pop() });
      toast.success('Media added'); setDialogOpen(false);
      setFormData({ filename: '', url: '', file_type: 'image', alt_text: '' }); fetchMedia();
    } catch (error) { toast.error('Failed to add media'); }
  };

  const handleDelete = async (id) => {
    try { await adminApi.deleteMedia(id); toast.success('Deleted'); fetchMedia(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success('URL copied!'); };

  return (
    <div className="space-y-6" data-testid="admin-media-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold">Media Library</h1><p className="text-muted-foreground">Manage your images and files</p></div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button><Plus className="w-4 h-4 mr-2" /> Add Media</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Media</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>URL *</Label><Input value={formData.url} onChange={(e) => setFormData(p => ({ ...p, url: e.target.value }))} placeholder="https://..." /></div>
              <div className="space-y-2"><Label>Filename</Label><Input value={formData.filename} onChange={(e) => setFormData(p => ({ ...p, filename: e.target.value }))} placeholder="image.jpg" /></div>
              <div className="space-y-2"><Label>Alt Text</Label><Input value={formData.alt_text} onChange={(e) => setFormData(p => ({ ...p, alt_text: e.target.value }))} /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit">Add</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="py-8 text-center">Loading...</div> : media.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">No media yet</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {media.map((m) => (
            <Card key={m.id} className="group overflow-hidden" data-testid={`media-${m.id}`}>
              <div className="aspect-square relative">
                <img src={m.url} alt={m.alt_text || m.filename} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button variant="secondary" size="icon" onClick={() => copyUrl(m.url)}><Copy className="w-4 h-4" /></Button>
                  <AlertDialog><AlertDialogTrigger asChild><Button variant="secondary" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(m.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <CardContent className="p-2"><p className="text-xs truncate">{m.filename}</p></CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
